import { ref, update } from "firebase/database";
import { type FormEvent, type ReactElement, useState } from "react";
import { useTranslation } from "react-i18next";
import { getFirebase } from "../firebase/init.js";

interface Props {
  tableId: string;
  initialTitle?: string;
  initialDesc?: string;
  onDone: () => Promise<void> | void;
}

export default function WizardPremiseStep(props: Props): ReactElement {
  const { t } = useTranslation();
  const [title, setTitle] = useState(props.initialTitle ?? "");
  const [desc, setDesc] = useState(props.initialDesc ?? "");
  const [busy, setBusy] = useState(false);

  async function handleSubmit(e: FormEvent): Promise<void> {
    e.preventDefault();
    const trimmedTitle = title.trim();
    const trimmedDesc = desc.trim();
    if (!trimmedTitle) return;
    setBusy(true);
    try {
      const { db } = getFirebase();
      const writes: Record<string, unknown> = {
        [`/tables/${props.tableId}/setting/premise/titleKey`]: trimmedTitle,
      };
      if (trimmedDesc) {
        writes[`/tables/${props.tableId}/setting/premise/descKey`] = trimmedDesc;
      } else {
        writes[`/tables/${props.tableId}/setting/premise/descKey`] = null;
      }
      await update(ref(db), writes);
      await props.onDone();
    } finally {
      setBusy(false);
    }
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-3">
      <h3 className="text-base font-semibold">{t("wizard.premise.title")}</h3>
      <p className="text-sm text-muted-foreground">{t("wizard.premise.desc")}</p>

      <label className="block text-sm">
        <span>{t("wizard.premise.title-label")}</span>
        <input
          className="mt-1 w-full rounded border px-2 py-1"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          aria-label={t("wizard.premise.title-label")}
          required
        />
      </label>

      <label className="block text-sm">
        <span>{t("wizard.premise.desc-label")}</span>
        <textarea
          className="mt-1 w-full rounded border px-2 py-1"
          rows={3}
          value={desc}
          onChange={(e) => setDesc(e.target.value)}
          aria-label={t("wizard.premise.desc-label")}
        />
      </label>

      <div className="flex justify-end">
        <button
          type="submit"
          className="rounded bg-emerald-600 px-3 py-1 text-sm text-white disabled:opacity-50"
          disabled={busy || title.trim().length === 0}
        >
          {busy ? t("wizard.premise.saving") : t("wizard.premise.finish")}
        </button>
      </div>
    </form>
  );
}
